export function createQuery(obj: any) {
  if (!obj) {
    return '';
  }
  const string = Object.entries(obj)
    .filter(([key, val]) => key !== null && val !== null)
    .map(([key, val]) => `${key}=${val}`)
    .join('&');
  return string.length ? `?${string}` : '';
}

/**
 * Remove URL query parameters like any potential path tokens from the current URL.
 */
export function removeQueryParamsFromCurrentUrl(state: any = null) {
  const urlObj = new URL(window.location.href);
  const searchParams = new URLSearchParams(window.location.search);

  // Get prerender flag and flavor from URL
  const flavor = searchParams.get('flavor');
  const isPrerender = searchParams.has('prerender');
  const hasDevPanel = searchParams.has('devpanel');

  // Clear existing search parameters
  urlObj.search = '';

  // Add back the parameters we want to keep.
  // This is important when using tools like Lighthouse or Playwright (QA).
  if (flavor) {
    urlObj.searchParams.append('flavor', flavor);
  }
  if (isPrerender) {
    urlObj.searchParams.append('prerender', '');
  }
  if (hasDevPanel) {
    // URL param to disable the dev panel: devpanel=false
    urlObj.searchParams.append('devpanel', 'false');
  }

  history.replaceState(
    state,
    '',
    urlObj
      .toString()
      .replace(/flavor=feature%2F/g, 'flavor=feature/') // Replace encoded %2F with /
      .replace(/flavor=rc%2F/g, 'flavor=rc/') // Replace encoded %2F with /
      .replace(/prerender=/g, 'prerender'), // Remove trailing = from prerender
  );
}

/**
 * Concatenate multiple paths into a single path, ensuring no double slashes
 * Examples:
 * - concatenatePath('/', '/path') => '/path'
 * - concatenatePath('http://example.com', '/path') => 'http://example.com/path'
 */
export function concatenatePath(...paths: string[]): string {
  return paths.map((p) => p.replace(/\/+$/, '')).join('/');
}

/**
 * Sanitize a value intended for use as a single URL path segment.
 * Prevents directory traversal attacks by:
 * 1. Decoding any percent-encoded sequences so traversal payloads like
 *    `%2f..%2f` are normalised before inspection.
 * 2. Removing path separators (`/`, `\`), query/fragment delimiters
 *    (`?`, `#`), newlines, and residual `%` signs.
 * 3. Removing traversal sequences (`..`).
 *
 * Legitimate characters (colons, hyphens, alphanumerics) are preserved
 * because gameIds use formats like `playngo:310`.
 *
 * Returns an empty string when the input is nullish or suspiciously encoded.
 */
export function sanitizePathSegment(value: string | undefined | null): string {
  if (value === null || value === undefined) return '';

  const original = String(value);
  let decoded = original;

  try {
    let prev: string;
    let iterations = 0;
    do {
      prev = decoded;
      decoded = decodeURIComponent(decoded);
      iterations++;
      if (iterations >= 10) return ''; // suspiciously deep encoding — fail closed
    } while (prev !== decoded);
  } catch {
    // Malformed URI component — sanitize the raw value rather than partial decode
    decoded = original;
  }

  // Strip path-dangerous characters.
  // Null byte stripped defensively; browsers also reject them in fetch() URLs natively.
  // Built via char codes to avoid linter control-character restrictions.
  const nul = String.fromCodePoint(0);
  let sanitized = decoded.replaceAll(new RegExp(`[/\\\\?#${nul}\\r\\n%]`, 'g'), '');

  // Remove traversal sequences — loop until stable to prevent re-assembly (e.g. '....' → '..')
  while (sanitized.includes('..')) {
    sanitized = sanitized.replaceAll('..', '');
  }

  return sanitized;
}

/**
 * Build a CSS `url()` value from a caller-supplied image URL.
 *
 * These URLs arrive from CMS/API payloads and are interpolated into CSS
 * custom properties. Embedded raw, a value containing `")` terminates the
 * `url()` token and injects arbitrary declarations into every rule that
 * consumes the property, so the input is validated rather than escaped:
 *
 * 1. Parsed by the URL parser, which percent-encodes quotes, backslashes
 *    and whitespace, and strips control characters.
 * 2. Restricted to `http:` / `https:` — any other scheme, including
 *    `data:` and `javascript:`, fails closed.
 * 3. Quotes and backslashes re-encoded defensively, so the result is safe
 *    inside a double-quoted CSS string regardless of the scheme-specific
 *    serialization rules the parser applied.
 *
 * Returns `none` for nullish, empty or rejected input. That is a valid
 * value for every `background-image` / `background` consumer, and it also
 * avoids the request for `url("undefined")` that an unguarded template
 * literal produces when the field is absent.
 */
export function cssUrlValue(src: string | undefined | null): string {
  if (!src) return 'none';

  let url: URL;
  try {
    // `new URL` + catch rather than `URL.canParse`: canParse needs
    // Safari 17, above the `iOS >= 16.4` floor in browserslist.
    url = new URL(src, document.baseURI);
  } catch {
    return 'none';
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'none';

  const safeHref = url.href.replaceAll('"', '%22').replaceAll('\\', '%5C');
  return `url("${safeHref}")`;
}
